import {ErrorMessage, useField} from 'formik';
import _ from 'lodash';
import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {components} from 'react-select';

import {useFormatterById} from '../../FormattersContext';
import ToJsonFormatter from './ToJsonFormatter';

import {unwrapNullableDataType} from '../../../ducks/formattersettingshelpers';
import {Select} from '../../../ui-toolkit';
import {FormErrorMessage} from '../../../ui-toolkit/forms/form-features/FormErrorMessage';

// As a convention, the enum type can use a slash to group options together.
// For example, you may have options like computers/laptop, computers/desktop, accessories/mouse, accessories/keyboard, et cetera
// In the dropdown we show these as two groups (computers and accessories) with sub-options
// This function converts the enum options into react-select options that are grouped this way
function groupOptions(enumOptions, getLabel) {
    const grouped = _.mapValues(
        _.groupBy(enumOptions, (eo) => {
            const parts = eo.value.split('/', 2);
            if (parts.length > 1) {
                return parts[0];
            }
            return null;
        }),
        (opts) => _.map(opts, 'value'),
    );

    const groups = _.keys(grouped);

    return _.flatten(
        _.map(groups, (g) => {
            if (g != 'null') {
                return [
                    {
                        label: getLabel(g),
                        options: _.sortBy(
                            [
                                ..._.map(grouped[g], (opt) => ({
                                    label: getLabel(opt),
                                    value: opt,
                                })),
                                // if there is an option e.g. "computers/laptop" and also an option "computers", then for the sake of logical display, we consider the "computers" options to be in the group "computers", and not a top-level option
                                ...(_.some(enumOptions, (eo) => eo.value == g) ? [{label: getLabel(g), value: g}] : []),
                            ],
                            'label',
                        ),
                    },
                ];
            } else {
                // here we filter out the options that were added because of the "category root" thingie of the previous comment
                const rootOptions = _.filter(grouped[g], (opt) => !_.includes(groups, opt));
                return _.sortBy(
                    _.map(rootOptions, (opt) => ({
                        label: getLabel(opt),
                        value: opt,
                    })),
                    'label',
                );
            }
        }),
    );
}

// if the enum formatter has settings, try to extract the display name of the given option, so that the user can search on it
function enumFormatterLabel(enumFormatterConfig) {
    return (value) => _.get(_.get(enumFormatterConfig, 'settings.displayNames'), value, value);
}

/**
 * A component that renders a dropdown form field for selecting an enum value. The data type provided to this component
 * can be either `EnumDataType` or a `NullableDataType` that wraps an `EnumDataType`.
 */
function EnumDropdownEditor({formatter, fieldName, entityType}) {
    const formatterById = useFormatterById(formatter.settings.entryFormatterId);
    const {t} = useTranslation();
    const enumDataType = useMemo(() => unwrapNullableDataType(entityType), [entityType]);

    const [FieldFormatter, subFormatter] = useMemo(() => {
        let subFormatter = formatterById;
        let FieldFormatter = {Formatter: ToJsonFormatter};
        if (subFormatter) FieldFormatter = subFormatter;
        return [FieldFormatter, subFormatter];
    }, [formatter.settings.entryFormatterId, formatterById]);

    const [, meta, helpers] = useField(fieldName);

    const options = useMemo(
        () => groupOptions(enumDataType.enumOptions, enumFormatterLabel(subFormatter)),
        [enumDataType.enumOptions, subFormatter],
    );

    const SingleValue = useCallback(
        (props) => (
            <components.SingleValue {..._.omit(props, ['children'])}>
                <FieldFormatter.Formatter formatter={subFormatter} entity={props.data.value} />
            </components.SingleValue>
        ),
        [subFormatter],
    );

    const Option = useCallback(
        (props) => (
            <components.Option {..._.omit(props, ['children'])}>
                <FieldFormatter.Formatter formatter={subFormatter} entity={props.value} />
            </components.Option>
        ),
        [subFormatter],
    );

    const onChange = useCallback(
        (val) => {
            helpers.setValue(val.value);
        },
        [helpers],
    );

    return (
        <>
            {/* Todo: Migrate to V2. This component is a raw one using Formik error messages. We need to make some refactors in order for the Select to be usable with Formik. */}
            <Select
                raw
                placeholder={t('searchableDropdown.placeholder')}
                options={options}
                components={{
                    Option,
                    SingleValue,
                }}
                onChange={onChange}
                value={{value: meta.value, label: meta.value}}
                validationError={<ErrorMessage name={fieldName} component={FormErrorMessage} />}></Select>
        </>
    );
}

export default EnumDropdownEditor;
